import { computed, Injectable, Signal, signal } from "@angular/core";
import { Observable, of } from "rxjs";
import { environment } from "../../../environments/environment";
import { FEATURE_FLAGS } from "../../app.constants";
import { LoginUserOfflineService } from "../offline/login-user-offline.service";
import { FeatureFlagContractService } from "./feature-flag.contract.service";
import { EnvironmentName, FeatureFlagDefinition, FeatureFlagsConfig, FeatureFlagStrategy } from "./feature-flag.types";
import featureFlagsJson from "./feature-flags.json";
import { STANDALONE } from "../../_offline/standalone-data/standalone-data";

type FeatureFlagDict = { [featureFlagName: string]: boolean };

@Injectable({
  providedIn: "root"
})
export class FeatureFlagOfflineService implements FeatureFlagContractService {
  private featureFlags = signal<FeatureFlagDict>({});
  private featureFlagsConfig: FeatureFlagsConfig;

  constructor(private loginUserService: LoginUserOfflineService) {
    this.featureFlagsConfig = featureFlagsJson as FeatureFlagsConfig;
    const evaluatedFlags = this.evaluateAllFlags();
    this.featureFlags.set(evaluatedFlags);
  }

  fetchAndUpdate(): Observable<void> {
    this.featureFlagsConfig = featureFlagsJson as FeatureFlagsConfig;
    const evaluatedFlags = this.evaluateAllFlags();
    this.featureFlags.set(evaluatedFlags);
    return of(void 0);
  }

  isFlagEnabled(flagName: FEATURE_FLAGS): Signal<boolean> {
    return computed(() => this.featureFlags()[flagName] ?? false);
  }

  private evaluateAllFlags(): FeatureFlagDict {
    const result: FeatureFlagDict = {};
    const currentEnv = environment.ENV as EnvironmentName;
    const tenantId = this.loginUserService.tenantId ?? STANDALONE.TENANT_ID;

    for (const feature of this.featureFlagsConfig.features) {
      result[feature.name] = this.evaluateFeature(feature, currentEnv, tenantId);
    }

    return result;
  }

  private evaluateFeature(feature: FeatureFlagDefinition, env: EnvironmentName, tenantId: string): boolean {
    const envConfig = feature.environments?.[env];

    if (!envConfig || !envConfig.enabled) {
      return false;
    }

    return this.evaluateStrategies(envConfig.strategies, tenantId);
  }

  private evaluateStrategies(strategies: FeatureFlagStrategy[], tenantId: string): boolean {
    if (!strategies || strategies.length === 0) {
      return false;
    }

    for (const strategy of strategies) {
      if (this.evaluateStrategy(strategy, tenantId)) {
        return true;
      }
    }

    return false;
  }

  private evaluateStrategy(strategy: FeatureFlagStrategy, tenantId: string): boolean {
    switch (strategy.name) {
      case "default":
        return true;
      case "userWithId":
        return this.evaluateUserWithIdStrategy(strategy, tenantId);
      default:
        return false;
    }
  }

  private evaluateUserWithIdStrategy(strategy: FeatureFlagStrategy, tenantId: string): boolean {
    const userIds = strategy.parameters?.userIds;
    if (!userIds || !tenantId) {
      return false;
    }

    const allowedTenantIds = userIds.split(",").map((id) => id.trim().toLowerCase());
    return allowedTenantIds.includes(tenantId.toLowerCase());
  }
}
